import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SKIP_GLOBAL_ERROR_TOAST } from '../helpers/http-context.tokens';
import { ESTAMP_API_PREFIX } from '../helpers/estamp-api-key.interceptor';

export interface CreateEstampOrderRequest {
  stateId: string;
  denomination: string | number;
  purpose: string;
  appendStampPosition: string;
  secondParty: string;
  firstParty: string;
  file: File;
}

/**
 * Demo eStamp API client.
 *
 * Every method here maps to exactly one request of the shipped
 * `eSTAMP-API-Postman-Collection.json`. Nothing outside that collection is
 * called by the demo eStamp pages.
 *
 * Authentication is the eStamp `x-api-key` only — see
 * {@link EstampApiKeyInterceptor}.
 */
@Injectable({
  providedIn: 'root'
})
export class EstampApiService {

  private readonly baseUrl = ESTAMP_API_PREFIX;

  constructor(private http: HttpClient) { }

  /** 1. GET eSTAMP STATES LIST — GET /bulk-estamp-api/state-list */
  getStates() {
    return this.http.get(`${this.baseUrl}/state-list`);
  }

  /** 2. CREATE eSTAMP ORDER — POST /bulk-estamp-api/order */
  createOrder(
    request: CreateEstampOrderRequest,
    showInlineError: boolean = false
  ) {
    return this.http.post(
      `${this.baseUrl}/order`,
      this.buildOrderFormData(request),
      {
        context: new HttpContext().set(
          SKIP_GLOBAL_ERROR_TOAST,
          showInlineError
        )
      }
    );
  }

  /** 3. GET eSTAMP ORDERS LIST — GET /bulk-estamp-api/client/orders?page&limit */
  getOrders(page: number, limit: number) {
    const params = new HttpParams()
      .set('page', page)
      .set('limit', limit);

    return this.http.get(`${this.baseUrl}/client/orders`, { params });
  }

  /** 5. GET eSTAMP ORDER DETAILS — GET /bulk-estamp-api/client/orders/{orderId} */
  getOrderDetail(orderId: string) {
    return this.http.get(`${this.baseUrl}/client/orders/${orderId}`);
  }

  /**
   * 6. GET eSTAMP INVENTORY — GET
   * /bulk-estamp-api/client/scanned-stamps?page&limit
   */
  getScannedStamps(page: number, limit: number) {
    const params = new HttpParams()
      .set('page', page)
      .set('limit', limit);

    return this.http.get(`${this.baseUrl}/client/scanned-stamps`, { params });
  }

  /**
   * Keeps the multipart body byte-for-byte compatible with the collection:
   * `stateId`, `denomination`, `purpose`, `appendStampPosition`,
   * `secondParty` and the `file` part — nothing else is sent.
   */
  private buildOrderFormData(
    request: CreateEstampOrderRequest
  ): FormData {

    const formData = new FormData();

    formData.append('stateId', String(request.stateId ?? '').trim());
    formData.append('denomination', String(request.denomination ?? '').trim());
    formData.append('purpose', String(request.purpose ?? '').trim());
    formData.append(
      'appendStampPosition',
      String(request.appendStampPosition ?? '').trim()
    );
    formData.append('secondParty', String(request.secondParty ?? '').trim());
    formData.append('firstParty', String(request.firstParty ?? '').trim());
    formData.append('file', request.file);

    return formData;
  }
}
